import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { LabourItemGroup } from '../../api/labourItemGroupsApi';
import { LabourGroupListItem } from '../molecules/LabourGroupListItem';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';

interface LabourGroupsListViewProps {
  groups: LabourItemGroup[];
  loading: boolean;
  onItemClick: (id: string) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  disabled?: boolean;
  onAddGroup?: () => void;
}

export const LabourGroupsListView: React.FC<LabourGroupsListViewProps> = ({
  groups,
  loading,
  onItemClick,
  onMenuOpen,
  disabled,
  onAddGroup,
}) => {
  if (groups.length === 0 && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          p: 4,
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400 }}>
          <WorkspacesIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.primary">
            No groups yet
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Labour groups organise items by trade (e.g. Brickwork, Joinery).
            Create groups to control which items workers see when booking.
          </Typography>
          {onAddGroup && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddGroup}
              data-testid="empty-state-add-group-button"
            >
              Add Group
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <ListViewShell
      itemCount={groups.length}
      loading={loading}
      emptyMessage="No groups found"
      columnHeaders={
        <>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Name
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: 75,
              textAlign: 'right',
              flexShrink: 0,
            }}
          >
            Items
          </Typography>
        </>
      }
    >
      {groups.map(group => (
        <LabourGroupListItem
          key={group.id}
          group={group}
          onItemClick={onItemClick}
          onMenuOpen={onMenuOpen}
          disabled={disabled}
        />
      ))}
    </ListViewShell>
  );
};
